import React, { useState } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const ContactContainer = styled.div`
  width: 100%;
  padding: 6rem 2rem;
  background: ${corporateTheme.backgroundGradient};
  position: relative;
  overflow: hidden;
  font-family: ${corporateTheme.fontFamily};
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.9) 100%),
              url('https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1926&q=80');
  background-size: cover;
  background-position: center;
  filter: brightness(1.1) contrast(0.8);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${corporateTheme.textSecondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactForm = styled(motion.form)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(20px);
  border-radius: 25px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const FormTitle = styled.h3`
  font-size: 1.8rem;
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: ${corporateTheme.textSecondary};
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${corporateTheme.background};
  border: 1px solid ${corporateTheme.cardBorder};
  border-radius: 10px;
  color: ${corporateTheme.textPrimary};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${corporateTheme.primary};
    background: rgba(30, 64, 175, 0.05);
  }
  
  &::placeholder {
    color: ${corporateTheme.textTertiary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${corporateTheme.background};
  border: 1px solid ${corporateTheme.cardBorder};
  border-radius: 10px;
  color: ${corporateTheme.textPrimary};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${corporateTheme.primary};
    background: rgba(30, 64, 175, 0.05);
  }
  
  option {
    background: ${corporateTheme.background};
    color: ${corporateTheme.textPrimary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${corporateTheme.background};
  border: 1px solid ${corporateTheme.cardBorder};
  border-radius: 10px;
  color: ${corporateTheme.textPrimary};
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${corporateTheme.primary};
    background: rgba(30, 64, 175, 0.05);
  }
  
  &::placeholder {
    color: ${corporateTheme.textTertiary};
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 40px rgba(30, 64, 175, 0.3);
    background: linear-gradient(135deg, ${corporateTheme.primaryDark} 0%, ${corporateTheme.primary} 100%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
`;

const SuccessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const SuccessText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
`;

const ErrorMessage = styled(motion.div)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(239, 68, 68, 0.3);
`;

const ContactInfo = styled(motion.div)`
  padding: 2rem;
`;

const InfoSection = styled.div`
  margin-bottom: 3rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(30, 64, 175, 0.05);
  border-radius: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(30, 64, 175, 0.1);
  }
`;

const InfoIcon = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.primary};
  min-width: 80px;
  font-weight: 600;
  background: ${corporateTheme.primaryGradient};
  padding: 0.5rem;
  border-radius: 20px;
  text-align: center;
  color: ${corporateTheme.background};
  letter-spacing: 0.5px;
`;

const InfoText = styled.div`
  color: ${corporateTheme.textSecondary};
  font-size: 1rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(30, 64, 175, 0.1);
  border-radius: 50%;
  color: ${corporateTheme.primary};
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(30, 64, 175, 0.2);
    transform: translateY(-2px);
  }
`;

const ContactSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [state, handleSubmit] = useForm("xpwrvwnw");

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: '',
    experience: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData(e.target);
    
    // Add custom fields
    formDataToSend.append('_subject', 'GrowTech Affiliate Application');
    formDataToSend.append('_replyto', formDataToSend.get('email'));
    formDataToSend.append('_next', 'javascript:void(0)'); // Disable redirect
    
    await handleSubmit(formDataToSend);
    
    // Reset form on success
    if (state.succeeded) {
      setFormData({
        name: '',
        email: '',
        company: '',
        interest: '',
        experience: '',
        message: ''
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <ContactContainer ref={ref} id="contact">
      <BackgroundImage />
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Header>
            <Title variants={itemVariants}>
              Start Your {corporateLabels.affiliate} Journey
            </Title>
            <Subtitle variants={itemVariants}>
              Join the GrowTech affiliate network and start maximizing your digital marketing revenue today
            </Subtitle>
          </Header>

          <ContentGrid>
            <ContactForm 
              variants={itemVariants} 
              onSubmit={onSubmit}
            >
              <FormTitle>Apply to Join Our Network</FormTitle>
              
              {state.succeeded && (
                <SuccessMessage
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <SuccessTitle>✨ Thank You!</SuccessTitle>
                  <SuccessText>
                    Your application has been submitted successfully! We'll review your information and get back to you within 24 hours.
                  </SuccessText>
                </SuccessMessage>
              )}
              
              {state.errors && state.errors.length > 0 && (
                <ErrorMessage
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  There was an error submitting your form. Please try again or email us directly at info@growtech.cc
                </ErrorMessage>
              )}
              
              <FormGroup>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
                <ValidationError prefix="Name" field="name" errors={state.errors} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                />
                <ValidationError prefix="Email" field="email" errors={state.errors} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="company">Company/Website</Label>
                <Input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your company or website URL"
                />
                <ValidationError prefix="Company" field="company" errors={state.errors} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="interest">Primary Interest *</Label>
                <Select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select your interest</option>
                  <option value="advertiser">Advertiser Partnership</option>
                  <option value="affiliate">Affiliate Partnership</option>
                  <option value="ecommerce">E-commerce Affiliate Programs</option>
                  <option value="saas">SaaS Affiliate Programs</option>
                  <option value="content">Content & SEO Services</option>
                  <option value="other">Other Services</option>
                </Select>
                <ValidationError prefix="Interest" field="interest" errors={state.errors} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="experience">Experience Level</Label>
                <Select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                >
                  <option value="">Select your experience level</option>
                  <option value="beginner">Beginner (0-1 years)</option>
                  <option value="intermediate">Intermediate (1-3 years)</option>
                  <option value="advanced">Advanced (3-5 years)</option>
                  <option value="expert">Expert (5+ years)</option>
                </Select>
                <ValidationError prefix="Experience" field="experience" errors={state.errors} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Additional Information</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your goals, current traffic, or any specific questions..."
                />
                <ValidationError prefix="Message" field="message" errors={state.errors} />
              </FormGroup>

              <SubmitButton
                type="submit"
                disabled={state.submitting}
                whileHover={{ scale: state.submitting ? 1 : 1.02 }}
                whileTap={{ scale: state.submitting ? 1 : 0.98 }}
              >
                {state.submitting ? 'Submitting...' : 'Apply Now'}
              </SubmitButton>
              
              <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#64748b', textAlign: 'center' }}>
                * Required fields. We'll respond within 24 hours.<br />
                For immediate assistance, email us at <strong>info@growtech.cc</strong>
              </p>
            </ContactForm>

            <ContactInfo variants={itemVariants}>
              <InfoSection>
                <InfoTitle>Get in Touch</InfoTitle>
                <InfoItem>
                  <InfoIcon>EMAIL</InfoIcon>
                  <InfoText>info@growtech.cc</InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>SUPPORT</InfoIcon>
                  <InfoText>Email Support - 24h Response</InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>COMPANY</InfoIcon>
                  <InfoText>GrowTech Ltd - Hong Kong</InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>FOCUS</InfoIcon>
                  <InfoText>Eastern Asian Markets</InfoText>
                </InfoItem>
              </InfoSection>

              <InfoSection>
                <InfoTitle>Quick Stats</InfoTitle>
                <InfoItem>
                  <InfoIcon>SPEED</InfoIcon>
                  <InfoText>Average 72h approval time</InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>REVENUE</InfoIcon>
                  <InfoText>Up to 45% revenue share</InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>TRACKING</InfoIcon>
                  <InfoText>Real-time tracking & reporting</InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>CONVERT</InfoIcon>
                  <InfoText>30% higher conversion rates</InfoText>
                </InfoItem>
              </InfoSection>

              <InfoSection>
                <InfoTitle>Follow Us</InfoTitle>
                <SocialLinks>
                  <SocialLink href="#" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    FB
                  </SocialLink>
                  <SocialLink href="#" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    TW
                  </SocialLink>
                  <SocialLink href="#" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    IG
                  </SocialLink>
                  <SocialLink href="#" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    LN
                  </SocialLink>
                </SocialLinks>
              </InfoSection>
            </ContactInfo>
          </ContentGrid>
        </motion.div>
      </Container>
    </ContactContainer>
  );
};

export default ContactSection; 